// web/src/utils/labels/misc-meta.ts —— 情绪 / 意图来源 / 收号来源 / 数据源测试 / 核验与账号状态 / 节点色
// 拆自 web/src/utils/labels.ts 的同名分区（内容原样搬运）；由 web/src/utils/labels.ts 以 export * 聚合，导出面对调用方不变。

import { maskPhone } from '../format';
import type { PillColor } from '../../components/PillTag';

import { IVR_DEST_TYPE_LABELS } from './flow-ivr-satisfaction';
import { attendanceStatusMeta, complaintTypeMeta } from './quality';
import { WORKFLOW_NODE_TYPE_LABELS, rbacOrgScopeLabel, rbacScopeTypeLabel } from './rbac-platform';
import { TICKET_SOURCE_LABELS } from './ticket-outbound-notify';

/** 情绪（实时情绪四档：正面/中性/焦虑/愤怒）→ [文案, PillColor]（色与 utils/quality.emotionTagColor 同口径） */
export const EMOTION_LABELS: Record<string, [string, PillColor]> = {
  正面: ['正面', 'green'],
  中性: ['中性', 'gray'],
  焦虑: ['焦虑', 'orange'],
  愤怒: ['愤怒', 'red'],
};

export function emotionMeta(v?: string): [string, PillColor] {
  return EMOTION_LABELS[v || ''] || [v || '-', 'gray'];
}

/** 工单来源 → [文案, PillColor]：AI 来源（ai_* 与坐席一键）统一电光青，与工单页 AiBadge 同色 */
export function ticketSourceMeta(v?: string): [string, PillColor] {
  const key = v || '';
  if (!key) return ['-', 'gray'];
  const color: PillColor = key.startsWith('ai_') || key === 'assist' ? 'cyan' : 'gray';
  return [TICKET_SOURCE_LABELS[key] || key, color];
}

/** 意图识别来源（ai_intent|ai_llm|ai_fallback|ai_agent）→ [文案, PillColor]（色沿用意图统计/测试台） */
const INTENT_SOURCE_COLORS: Record<string, PillColor> = {
  ai_intent: 'arcoblue',
  ai_llm: 'cyan',
  ai_fallback: 'gray',
  ai_agent: 'green',
};

export function intentSourceMeta(v?: string): [string, PillColor] {
  const key = v || '';
  if (!key) return ['-', 'gray'];
  return [TICKET_SOURCE_LABELS[key] || key, INTENT_SOURCE_COLORS[key] || 'gray'];
}

/** 采集字段来源（ai|crm|agent）→ [文案, PillColor] */
export const COLLECT_SOURCE_LABELS: Record<string, [string, PillColor]> = {
  ai: ['AI 提取', 'arcoblue'],
  crm: ['资料带出', 'gray'],
  agent: ['坐席填写', 'gray'],
};

export function collectSourceMeta(v?: string): [string, PillColor] {
  return COLLECT_SOURCE_LABELS[v || ''] || [v || '-', 'gray'];
}

/** 数据源连通性测试结果（success|business_error|fail）→ [文案, PillColor] */
export const DATASOURCE_TEST_RESULT_LABELS: Record<string, [string, PillColor]> = {
  success: ['成功', 'green'],
  business_error: ['业务错误', 'orange'],
  fail: ['失败', 'red'],
};

export function datasourceTestResultMeta(v?: string): [string, PillColor] {
  return DATASOURCE_TEST_RESULT_LABELS[v || ''] || [v || '-', 'gray'];
}

/**
 * 身份核验等级（0-3）→ 文案：0 不核验 / 1 号码绑定 / 2 身份证尾号 / 3 短信验证码。
 * 取值依据 api/datasource.ts 的 verify_level 注释（0=不核验 1=号码绑定 2=尾号(DTMF/语音) 3=短信码），
 * 括号内实现细节（本网号段 / DTMF/语音）属说明，不写进列文案。
 */
export function verifyLevelLabel(v?: number | string | null): string {
  if (v === undefined || v === null || v === '') return '-';
  const key = String(v);
  const map: Record<string, string> = { '0': '不核验', '1': '号码绑定', '2': '身份证尾号', '3': '短信验证码' };
  return map[key] || key;
}

/**
 * 账号状态（active|disabled|locked）→ [文案, PillColor]。
 * 锁定态由 locked_until 未到期推导（页面判定后传 'locked'），禁用优先级高于锁定。
 */
export const ACCOUNT_STATUS_LABELS: Record<string, [string, PillColor]> = {
  active: ['启用', 'green'],
  disabled: ['禁用', 'red'],
  locked: ['锁定', 'red'],
};

export function accountStatusMeta(v?: string): [string, PillColor] {
  return ACCOUNT_STATUS_LABELS[v || ''] || [v || '-', 'gray'];
}

/** 审批流程节点类型 → [文案, PillColor]（色沿用流程模板页节点色；图标仍由页面持有） */
const WORKFLOW_NODE_TYPE_COLORS: Record<string, PillColor> = {
  start: 'green',
  approve: 'arcoblue',
  task: 'gold',
  notify: 'gray',
  condition: 'orange',
  end: 'red',
};

export function workflowNodeTypeMeta(v?: string): [string, PillColor] {
  const key = v || '';
  if (!key) return ['-', 'gray'];
  return [WORKFLOW_NODE_TYPE_LABELS[key] || key, WORKFLOW_NODE_TYPE_COLORS[key] || 'gray'];
}

/** IVR 按键目的地类型 → [文案, PillColor]（图标/分组仍由页面持有） */
const IVR_DEST_TYPE_COLORS: Record<string, PillColor> = {
  bot: 'arcoblue',
  queue: 'green',
  extension: 'orange',
  menu: 'arcoblue',
  back: 'gray',
  repeat: 'cyan',
  hangup: 'red',
};

export function ivrDestTypeMeta(v?: string): [string, PillColor] {
  const key = v || '';
  if (!key) return ['-', 'gray'];
  return [IVR_DEST_TYPE_LABELS[key] || key, IVR_DEST_TYPE_COLORS[key] || 'gray'];
}

/** 投诉类型文案（等价 complaintTypeMeta()[0]） */
export function complaintTypeLabel(v?: string): string {
  return complaintTypeMeta(v)[0];
}

/** 角色数据范围类型 → 文案（等价 rbacScopeTypeLabel） */
export function roleScopeTypeLabel(v?: string): string {
  return rbacScopeTypeLabel(v);
}

/** 角色组织范围 → 文案（等价 rbacOrgScopeLabel） */
export function roleScopeLabel(v?: string): string {
  return rbacOrgScopeLabel(v);
}

/** 考勤对账状态 → [文案, PillColor]（等价 attendanceStatusMeta） */
export function shiftAttendanceMeta(v?: string): [string, PillColor] {
  return attendanceStatusMeta(v);
}
